import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { DappaApisStore } from '../../pages/dappa-apis-page/dappa-apis.store';
import { DappaApi } from '../../shared/interfaces/dappa.interface';

/**
 * The header's quick-jump while the user is on the Dappa API pages (EHQ-339):
 * it takes the workflow dropdown's place and switches between APIs the way that
 * one switches between workflows. It borrows the workflow dropdown's styles so
 * the two read as the same control. A failed list load (a 404 when scripting
 * is off, say) shows in the menu with a retry, as the APIs page does.
 */
@Component({
  selector: 'app-dappa-api-dropdown',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DappaApisStore],
  imports: [MatIconModule],
  templateUrl: './dappa-api-dropdown.component.html',
  styleUrl: '../workflow-dropdown/workflow-dropdown.component.scss',
})
export class DappaApiDropdownComponent {
  protected readonly store = inject(DappaApisStore);
  readonly #router = inject(Router);

  /** The API the user is looking at, or undefined on the APIs list. */
  readonly selectedApiId = input<number | undefined>(undefined);

  protected readonly isOpen = signal(false);

  protected readonly label = computed(() => {
    const id = this.selectedApiId();
    if (id === undefined) {
      return 'All APIs';
    }
    return this.store.apis().find((a) => a.apiId === id)?.name ?? 'APIs';
  });

  toggleDropdown(): void {
    if (!this.isOpen()) {
      // The pages own their own store; refetch so an API created there shows up.
      this.store.reload();
    }
    this.isOpen.update((open) => !open);
  }

  closeDropdown(): void {
    this.isOpen.set(false);
  }

  selectAll(): void {
    this.closeDropdown();
    void this.#router.navigate(['/project', this.store.projectId(), 'apis'], {
      queryParamsHandling: 'preserve',
    });
  }

  selectApi(api: DappaApi): void {
    this.closeDropdown();
    void this.#router.navigate(['/project', this.store.projectId(), 'apis', api.apiId], {
      queryParamsHandling: 'preserve',
    });
  }

  // True for Enter/Space, so the template can write `isActivationKey($event) && action()`
  // without allocating a bound function on every change detection.
  protected isActivationKey(event: KeyboardEvent): boolean {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      return true;
    }
    if (event.key === 'Escape') {
      this.closeDropdown();
    }
    return false;
  }
}
